import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { AreaChart, BarChart } from '../lib/charts';
import { Card, CardHead, ErrorState, Kpi, Loading, Section } from '../components/ui';
import { delta, gbBytes, hrs, n0, n1, plural } from '../lib/format';
import WorkspaceDrawer from './WorkspaceDrawer';

const WEEKDAYS = { 1: 'пн', 2: 'вт', 3: 'ср', 4: 'чт', 5: 'пт', 6: 'сб', 7: 'вс' };

export default function Content({ days, nonce }) {
  const [openId, setOpenId] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.content({ days }), [days, nonce]);
  const comments = useApi(() => api.series({ days, metric: 'comments' }), [days, nonce]);
  const videos = useApi(() => api.series({ days, metric: 'videos' }), [days, nonce]);

  if (loading) return <Loading what="контент" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const p = data.period;
  const a = data.averages;
  const dayWord = plural(days, 'день', 'дня', 'дней');

  return (
    <>
      <div className="grid cards-4">
        <Kpi label={`видео залито за ${days} ${dayWord}`} value={n0(p.videos)}
          delta={delta(p.videos, p.videosPrev)} note={`${n1(p.videos / days)} в день`} />
        <Kpi label={`хронометраж за ${days} ${dayWord}`} value={hrs(p.durationSec)}
          delta={delta(p.durationSec, p.durationSecPrev)}
          note={`в среднем ${hrs(p.durationSec / Math.max(1, p.videos))} на видео`} />
        <Kpi label={`комментариев за ${days} ${dayWord}`} value={n0(p.comments)}
          delta={delta(p.comments, p.commentsPrev)}
          note={`${n0(p.comments / days)} в день, ${n0(p.comments / (days / 7))} в неделю`} />
        <Kpi label="всего накоплено" value={n0(data.totals.videos)} unit="видео"
          note={`${hrs(data.totals.durationSec)} · ${n0(data.totals.comments)} комментариев`} />
      </div>

      <Section title="комментарии" hint={days > 90 ? 'по неделям за выбранный период' : 'по дням за выбранный период'} />
      <Card className="pad">
        {comments.loading ? <Loading what="график" />
          : comments.error ? <ErrorState error={comments.error} onRetry={comments.reload} />
          : <BarChart height={180} color="#9a6aff" data={comments.data.map((d) => ({ date: d.date, v: d.value }))} />}
      </Card>

      <div className="grid cards-2" style={{ marginTop: 12 }}>
        <Card className="pad">
          <CardHead title="загрузки видео" caption={days > 90 ? 'по неделям' : 'по дням'} />
          {videos.loading ? <Loading what="график" />
            : videos.error ? <ErrorState error={videos.error} onRetry={videos.reload} />
            : <AreaChart height={160} color="#427fff" data={videos.data.map((d) => ({ date: d.date, v: d.value }))} />}
        </Card>

        <Card className="pad">
          <CardHead title="по дням недели" caption={`среднее число комментариев, ${Math.min(days, 90)} дней`} />
          <BarChart height={160} color="#6cb8ff" data={data.byWeekday.map((d) => ({ label: WEEKDAYS[d.weekday], v: d.comments }))} />
          <div className="legend">
            <span>в выходные активность падает примерно втрое — это нормально для студийной работы</span>
          </div>
        </Card>
      </div>

      <div className="grid cards-2" style={{ marginTop: 12 }}>
        <Card className="pad">
          <CardHead title="больше всего материала" caption="общий хронометраж по РП" />
          <div className="list">
            {data.topByDuration.map((o) => (
              <button className="row" key={o.id} onClick={() => setOpenId(o.id)}>
                <span className="rn">{o.title}<div className="t-sub">{n0(o.videos)} видео · {gbBytes(o.sizeBytes)}</div></span>
                <span className="rv">{hrs(o.durationSec)}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="в среднем" caption="по живым РП" />
          <div className="stat-lines">
            <div className="sl"><span className="sl-v">{n1(a.videosPerWorkspace)}</span><span className="sl-l">видео на пространство</span></div>
            <div className="sl"><span className="sl-v">{hrs(a.secondsPerVideo)}</span><span className="sl-l">длительность одного видео</span></div>
            <div className="sl"><span className="sl-v">{n1(a.commentsPerVideo)}</span><span className="sl-l">комментариев на видео</span></div>
            <div className="sl"><span className="sl-v">{gbBytes(a.bytesPerVideo)}</span><span className="sl-l">вес одного видео</span></div>
          </div>
        </Card>
      </div>

      <WorkspaceDrawer id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
